import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "";

const S = {
  page: { minHeight: "100vh", background: "#f3f4f6", padding: "1.5rem", fontFamily: "inherit" },
  card: { background: "#fff", borderRadius: 12, padding: "1.25rem", marginBottom: "1rem", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", maxWidth: 560 },
  h: { margin: "0 0 1rem", fontSize: "1.1rem", fontWeight: 800 },
  stat: { display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: "1px solid #f3f4f6", fontSize: "0.9rem" },
  btn: { padding: "0.6rem 1.2rem", borderRadius: 8, border: "none", background: "#8b5cf6", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: "0.9rem" },
  btnGreen: { background: "#10b981" },
  btnDisabled: { opacity: 0.5, cursor: "not-allowed" },
  input: { width: "100%", padding: "0.55rem", borderRadius: 8, border: "1px solid #d1d5db", marginBottom: "0.6rem", fontSize: "0.9rem" },
  msg: (err) => ({ padding: "0.6rem", borderRadius: 8, fontSize: "0.85rem", marginBottom: "0.75rem", background: err ? "#fef2f2" : "#f0fdf4", color: err ? "#b91c1c" : "#166534" }),
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50 },
  modal: { background: "#fff", borderRadius: 14, padding: "1.5rem", maxWidth: 380, width: "90%" },
  nav: { marginBottom: "1rem" },
};

function PromoManagement() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [isError, setIsError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [releasing, setReleasing] = useState(false);
  const [tid, setTid] = useState("");
  const [amount, setAmount] = useState("");
  const [desc, setDesc] = useState("");
  const [adding, setAdding] = useState(false);
  const [playerInfo, setPlayerInfo] = useState(null);
  const adminToken = localStorage.getItem("adminToken");
  const auth = { headers: { Authorization: `Bearer ${adminToken}` } };

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/promo/status`, auth);
      setStatus(res.data);
    } catch (err) {
      setMessage("Failed to load promo status");
      setIsError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  useEffect(() => {
    setPlayerInfo(null);
    if (tid.length < 6) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_URL}/admin/promo/player/${tid}`, auth);
        setPlayerInfo(res.data);
      } catch {
        setPlayerInfo({ error: true });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [tid]);

  const handleRelease = async () => {
    setReleasing(true);
    try {
      const res = await axios.post(`${API_URL}/admin/promo/release`, {}, auth);
      setMessage(`Released promo to ${res.data.granted} players — ${res.data.total.toLocaleString()} Birr total`);
      setIsError(false);
      setConfirming(false);
      fetchStatus();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Release failed");
      setIsError(true);
    } finally {
      setReleasing(false);
    }
  };

  const handleAdd = async () => {
    if (!tid || !amount) { setMessage("Enter telegram ID and amount"); setIsError(true); return; }
    setAdding(true);
    try {
      const res = await axios.post(`${API_URL}/admin/promo/add`, {
        telegram_id: parseInt(tid, 10),
        amount: parseFloat(amount),
        description: desc || "Admin promo credit",
      }, auth);
      setMessage(`Promo credited — new promo balance: ${res.data.promo_balance} Birr`);
      setIsError(false);
      setTid(""); setAmount(""); setDesc("");
      fetchStatus();
    } catch (err) {
      setMessage(err.response?.data?.detail || "Failed to add promo");
      setIsError(true);
    } finally {
      setAdding(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.nav}>
        <Link to="/dashboard" style={{ color: "#6d28d9", fontWeight: 700, fontSize: "0.85rem" }}>← Dashboard</Link>
      </div>
      <h2 style={{ margin: "0 0 1rem" }}>🎁 Promo Wallet</h2>
      {message && <div style={{ ...S.msg(isError), maxWidth: 560 }}>{message}</div>}

      <div style={S.card}>
        <h3 style={S.h}>Compensation Restore</h3>
        {loading ? <p>Loading…</p> : (
          <>
            <div style={S.stat}><span>Players waiting</span><b>{status?.pending_count ?? 0}</b></div>
            <div style={S.stat}><span>Pending total</span><b>{(status?.pending_total ?? 0).toLocaleString()} Birr</b></div>
            <div style={S.stat}><span>Players credited</span><b>{status?.credited_count ?? 0}</b></div>
            <div style={S.stat}><span>Credited total</span><b>{(status?.credited_total ?? 0).toLocaleString()} Birr</b></div>
            <div style={S.stat}><span>Promo in wallets</span><b>{(status?.promo_outstanding ?? 0).toLocaleString()} Birr</b></div>
            <button
              style={{ ...S.btn, marginTop: "1rem", ...(status?.pending_count ? {} : S.btnDisabled) }}
              disabled={!status?.pending_count}
              onClick={() => setConfirming(true)}
            >
              🚀 Release Promo ({status?.pending_count ?? 0} players)
            </button>
          </>
        )}
      </div>

      <div style={S.card}>
        <h3 style={S.h}>Add Promo to Player</h3>
        <input style={S.input} placeholder="Telegram ID" value={tid} onChange={(e) => setTid(e.target.value.replace(/\D/g, ""))} />
        {playerInfo && (
          playerInfo.error ? (
            <div style={{ fontSize: "0.8rem", color: "#b91c1c", marginBottom: "0.6rem" }}>Player not found</div>
          ) : (
            <div style={{ fontSize: "0.8rem", color: "#374151", background: "#f5f3ff", border: "1px solid #ddd6fe", borderRadius: 8, padding: "0.5rem 0.75rem", marginBottom: "0.6rem" }}>
              <b>@{playerInfo.username || "—"}</b> · Balance: <b>{playerInfo.balance.toFixed(2)}</b> · Bonus: <b style={{ color: "#7c3aed" }}>{playerInfo.promo_balance.toFixed(2)}</b> Birr
            </div>
          )
        )}
        <input style={S.input} placeholder="Amount (max 1000)" type="number" min="1" max="1000" value={amount} onChange={(e) => setAmount(e.target.value)} />
        <input style={S.input} placeholder="Reason (optional)" value={desc} onChange={(e) => setDesc(e.target.value)} />
        <button style={{ ...S.btn, ...S.btnGreen, ...(adding ? S.btnDisabled : {}) }} disabled={adding} onClick={handleAdd}>
          {adding ? "Crediting…" : "＋ Add Promo"}
        </button>
      </div>

      {confirming && (
        <div style={S.overlay} onClick={() => !releasing && setConfirming(false)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ margin: "0 0 0.75rem" }}>Release promo to {status?.pending_count} players?</h3>
            <p style={{ fontSize: "0.9rem", color: "#374151" }}>
              This credits <b>{(status?.pending_total ?? 0).toLocaleString()} Birr</b> of play-only promo
              across <b>{status?.pending_count}</b> players (each gets their pre-reset balance, max 1,000).
              This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem" }}>
              <button style={{ ...S.btn, flex: 1 }} disabled={releasing} onClick={handleRelease}>
                {releasing ? "Releasing…" : "Yes, Release"}
              </button>
              <button style={{ ...S.btn, flex: 1, background: "#9ca3af" }} disabled={releasing} onClick={() => setConfirming(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PromoManagement;
